import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Wallet, Banknote, Loader2,
  RefreshCw, Users, TrendingUp, ChevronDown, ChevronUp,
  CreditCard, Settings, PlusCircle, Clipboard, FileText, Search,
} from "lucide-react";
import { Card } from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useAuth } from "../../../hooks/useAuth";
import {
  useAdminEncashments,
  useAdminWalletTransactions,
  useApproveEncashment,
  useReleaseEncashment,
  useRejectEncashment,
  useAdminWalletAdvances,
  useApproveWalletAdvance,
  useReleaseWalletAdvance,
  useRejectWalletAdvance,
  useWalletAdvanceConfig,
  useSetWalletAdvanceLimit,
  useAdminWalletCredits,
  useAddWalletCredit,
  useAdminServiceRequests,
  useApproveServiceRequest,
  useRejectServiceRequest,
  useVoidServiceRequest,
} from "../../../hooks/useWallet";
import { useBizUsers } from "../../../hooks/useBizAdmin";
import { useStoreServices } from "../../../hooks/useStore";
import { formatCurrency, formatDate } from "../../../utils/format";
import type { WalletEncashment, EncashmentStatus, WalletAdvance, WalletAdvanceStatus, ServiceRequest, ServiceRequestStatus, WalletBalance, WalletTransaction, WalletCredit } from "../../../lib/walletApi";
import { walletApi } from "../../../lib/walletApi";

// ── Helpers ───────────────────────────────────────────────────────────────────

const inputClass =
  "w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]";

const STATUS_STYLES: Record<string, { cls: string; label: string }> = {
  PENDING:  { cls: "bg-[#fef9c3] text-[#a16207]",  label: "Pending" },
  APPROVED: { cls: "bg-[#dbeafe] text-[#1d4ed8]",  label: "Approved" },
  RELEASED: { cls: "bg-[#dcfce7] text-[#15803d]",  label: "Released" },
  REJECTED: { cls: "bg-[#fee2e2] text-[#b91c1c]",  label: "Rejected" },
  PAID:     { cls: "bg-[#f0fdf4] text-[#15803d]",   label: "Paid" },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${s?.cls ?? "bg-[#f1f5f9] text-[#64748b]"}`}>
      {s?.label ?? status}
    </span>
  );
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

// ── Approve Modal ─────────────────────────────────────────────────────────────

function ApproveModal({
  enc,
  businessId,
  onClose,
}: {
  enc: WalletEncashment;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const approve = useApproveEncashment();
  const [comments, setComments] = useState("");

  const handleApprove = async () => {
    try {
      await approve.mutateAsync({
        encashId: enc.encashId,
        payload: { userId: enc.userId, businessId, comments: comments || undefined },
      });
      toast.success("Encashment approved!");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to approve. The request may have already been reviewed.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#f0fdf4] border border-[#bbf7d0] p-4">
          <p className="text-xs font-medium text-[#166534] mb-1">{enc.employeeName ?? "Employee"}</p>
          <p className="text-2xl font-bold text-[#15803d]">{formatCurrency(enc.amount)}</p>
          <p className="text-xs text-[#4ade80] mt-0.5">Requested {formatDate(enc.requestedAt)}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Comments (optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Add a note for the employee…"
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleApprove} disabled={approve.isPending} fullWidth>
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Release Modal ─────────────────────────────────────────────────────────────

function ReleaseModal({
  enc,
  businessId,
  onClose,
}: {
  enc: WalletEncashment;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const release = useReleaseEncashment();
  const [releasedAt, setReleasedAt] = useState(todayIso());
  const [comments, setComments] = useState("");

  const handleRelease = async () => {
    if (!releasedAt) { toast.error("Release date is required."); return; }
    try {
      await release.mutateAsync({
        encashId: enc.encashId,
        payload: { userId: enc.userId, businessId, releasedAt, comments: comments || undefined },
      });
      toast.success("Payment logged as released!");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to release. The request may have already been released.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#eff6ff] border border-[#bfdbfe] p-4">
          <p className="text-xs font-medium text-[#1e40af] mb-1">{enc.employeeName ?? "Employee"}</p>
          <p className="text-2xl font-bold text-[#1d4ed8]">{formatCurrency(enc.amount)}</p>
          <p className="text-xs text-[#93c5fd] mt-0.5">Approved encashment</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Release Date</label>
          <input
            type="date"
            value={releasedAt}
            onChange={(e) => setReleasedAt(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Comments (optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Reference number, payment method, etc."
            className={inputClass}
          />
        </div>
        <div className="rounded-lg bg-[#eff3ff] border border-[#c7d2fe] px-4 py-3 text-xs text-[#3730a3]">
          Logging a release will notify the employee that their cash is ready. This action cannot be undone.
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleRelease} disabled={release.isPending || !releasedAt} fullWidth>
            {release.isPending ? "Logging…" : "Log Cash Release"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────

function RejectModal({
  enc,
  businessId,
  onClose,
}: {
  enc: WalletEncashment;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const reject = useRejectEncashment();
  const [comments, setComments] = useState("");

  const handleReject = async () => {
    try {
      await reject.mutateAsync({
        encashId: enc.encashId,
        payload: { userId: enc.userId, businessId, comments: comments || undefined },
      });
      toast.success("Encashment rejected.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to reject. The request may have already been reviewed.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#fef2f2] border border-[#fecaca] p-4">
          <p className="text-xs font-medium text-[#991b1b] mb-1">{enc.employeeName ?? "Employee"}</p>
          <p className="text-2xl font-bold text-[#dc2626]">{formatCurrency(enc.amount)}</p>
          <p className="text-xs text-[#fca5a5] mt-0.5">Requested {formatDate(enc.requestedAt)}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Reason (optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Reason for rejection…"
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button
            onClick={handleReject}
            disabled={reject.isPending}
            fullWidth
            className="!bg-[#dc2626] hover:!bg-[#b91c1c]"
          >
            {reject.isPending ? "Rejecting…" : "Reject Request"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Encashment Row ────────────────────────────────────────────────────────────

function EncashmentRow({
  enc,
  businessId,
}: {
  enc: WalletEncashment;
  businessId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState<"approve" | "release" | "reject" | null>(null);

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("wallet:encashment", "approve");
  const canReject = canApproveAction("wallet:encashment", "reject");
  const canRelease = canApproveAction("wallet:encashment", "release");

  return (
    <>
      <tr
        className="hover:bg-[#f8fafc] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-5 py-3">
          <p className="text-sm font-semibold text-[#0f172a]">{enc.employeeName ?? enc.userId}</p>
          <p className="text-xs text-[#94a3b8]">{formatDate(enc.requestedAt)}</p>
        </td>
        <td className="px-5 py-3 text-sm font-bold text-[#0f172a]">{formatCurrency(enc.amount)}</td>
        <td className="px-5 py-3"><StatusBadge status={enc.status} /></td>
        <td className="px-5 py-3">
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {enc.status === "PENDING" && (
              <>
                <button
                  onClick={() => setModal("approve")}
                  disabled={!canApprove}
                  title={!canApprove ? "You don't have permission to approve" : undefined}
                  className="rounded-lg bg-[#2447d9] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Approve
                </button>
                <button
                  onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-semibold text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </>
            )}
            {enc.status === "APPROVED" && (
              <>
                <button
                  onClick={() => setModal("release")}
                  disabled={!canRelease}
                  title={!canRelease ? "You don't have permission to release" : undefined}
                  className="rounded-lg bg-[#15803d] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#166534] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Log Release
                </button>
                <button
                  onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-semibold text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </>
            )}
            {enc.status === "RELEASED" && (
              <span className="text-xs text-[#94a3b8]">
                Released {enc.releasedAt ? formatDate(enc.releasedAt) : ""}
                {enc.releasedBy ? ` by ${enc.releasedBy}` : ""}
              </span>
            )}
            {enc.status === "REJECTED" && (
              <span className="text-xs text-[#94a3b8]">
                Rejected {enc.rejectedAt ? formatDate(enc.rejectedAt) : ""}
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-3 text-[#94a3b8]">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#f8fafc]">
          <td colSpan={5} className="px-5 pb-3 pt-1">
            <div className="text-xs space-y-1 text-[#64748b]">
              {enc.approvedAt && <p><span className="font-medium">Approved:</span> {formatDate(enc.approvedAt)} by {enc.approvedBy}</p>}
              {enc.releasedAt && <p><span className="font-medium">Released:</span> {formatDate(enc.releasedAt)} by {enc.releasedBy}</p>}
              {enc.rejectedAt && <p><span className="font-medium">Rejected:</span> {formatDate(enc.rejectedAt)} by {enc.rejectedBy}</p>}
              {enc.comments && <p><span className="font-medium">Note:</span> {enc.comments}</p>}
            </div>
          </td>
        </tr>
      )}

      {modal === "approve" && (
        <Modal isOpen onClose={() => setModal(null)} title="Approve Encashment" size="sm">
          <ApproveModal enc={enc} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "release" && (
        <Modal isOpen onClose={() => setModal(null)} title="Log Cash Release" size="sm">
          <ReleaseModal enc={enc} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "reject" && (
        <Modal isOpen onClose={() => setModal(null)} title="Reject Encashment" size="sm">
          <RejectModal enc={enc} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
    </>
  );
}

// ── Approve Advance Modal ─────────────────────────────────────────────────────

function ApproveAdvanceModal({
  adv,
  businessId,
  onClose,
}: {
  adv: WalletAdvance;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const approveWalletAdvance = useApproveWalletAdvance();
  const [comments, setComments] = useState("");

  const handleApprove = async () => {
    try {
      await approveWalletAdvance.mutateAsync({
        advId: adv.advId,
        payload: { userId: adv.userId, businessId, comments: comments || undefined },
      });
      toast.success("Wallet advance approved!");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to approve. The request may have already been reviewed.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#f0fdf4] border border-[#bbf7d0] p-4">
          <p className="text-xs font-medium text-[#166534] mb-1">{adv.employeeName ?? "Employee"}</p>
          <p className="text-2xl font-bold text-[#15803d]">{formatCurrency(adv.amount)}</p>
          <p className="text-xs text-[#4ade80] mt-0.5">Requested {formatDate(adv.requestedAt)}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Comments (optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Add a note for the employee…"
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleApprove} disabled={approveWalletAdvance.isPending} fullWidth>
            {approveWalletAdvance.isPending ? "Approving…" : "Approve"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Release Advance Modal ─────────────────────────────────────────────────────

function ReleaseAdvanceModal({
  adv,
  businessId,
  onClose,
}: {
  adv: WalletAdvance;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const releaseWalletAdvance = useReleaseWalletAdvance();
  const [releasedAt, setReleasedAt] = useState(todayIso());
  const [comments, setComments] = useState("");

  const handleRelease = async () => {
    if (!releasedAt) { toast.error("Release date is required."); return; }
    try {
      await releaseWalletAdvance.mutateAsync({
        advId: adv.advId,
        payload: { userId: adv.userId, businessId, releasedAt, comments: comments || undefined },
      });
      toast.success("Advance released!");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to release. The request may have already been released.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#eff6ff] border border-[#bfdbfe] p-4">
          <p className="text-xs font-medium text-[#1e40af] mb-1">{adv.employeeName ?? "Employee"}</p>
          <p className="text-2xl font-bold text-[#1d4ed8]">{formatCurrency(adv.amount)}</p>
          <p className="text-xs text-[#93c5fd] mt-0.5">Approved wallet advance</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Release Date</label>
          <input
            type="date"
            value={releasedAt}
            onChange={(e) => setReleasedAt(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Comments (optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Reference number, payment method, etc."
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleRelease} disabled={releaseWalletAdvance.isPending || !releasedAt} fullWidth>
            {releaseWalletAdvance.isPending ? "Releasing…" : "Release Advance"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Reject Advance Modal ──────────────────────────────────────────────────────

function RejectAdvanceModal({
  adv,
  businessId,
  onClose,
}: {
  adv: WalletAdvance;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const rejectWalletAdvance = useRejectWalletAdvance();
  const [comments, setComments] = useState("");

  const handleReject = async () => {
    try {
      await rejectWalletAdvance.mutateAsync({
        advId: adv.advId,
        payload: { userId: adv.userId, businessId, comments: comments || undefined },
      });
      toast.success("Wallet advance rejected.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to reject. The request may have already been reviewed.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#fef2f2] border border-[#fecaca] p-4">
          <p className="text-xs font-medium text-[#991b1b] mb-1">{adv.employeeName ?? "Employee"}</p>
          <p className="text-2xl font-bold text-[#dc2626]">{formatCurrency(adv.amount)}</p>
          <p className="text-xs text-[#fca5a5] mt-0.5">Requested {formatDate(adv.requestedAt)}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Reason (optional)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Reason for rejection…"
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button
            onClick={handleReject}
            disabled={rejectWalletAdvance.isPending}
            fullWidth
            className="!bg-[#dc2626] hover:!bg-[#b91c1c]"
          >
            {rejectWalletAdvance.isPending ? "Rejecting…" : "Reject Request"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Advance Row ───────────────────────────────────────────────────────────────

function AdvanceRow({
  adv,
  businessId,
}: {
  adv: WalletAdvance;
  businessId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState<"approve" | "release" | "reject" | null>(null);

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("wallet:advance", "approve");
  const canReject = canApproveAction("wallet:advance", "reject");
  const canRelease = canApproveAction("wallet:advance", "release");

  return (
    <>
      <tr
        className="hover:bg-[#f8fafc] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-5 py-3">
          <p className="text-sm font-semibold text-[#0f172a]">{adv.employeeName ?? adv.userId}</p>
          <p className="text-xs text-[#94a3b8]">{formatDate(adv.requestedAt)}</p>
        </td>
        <td className="px-5 py-3 text-sm font-bold text-[#0f172a]">{formatCurrency(adv.amount)}</td>
        <td className="px-5 py-3 text-sm text-[#64748b]">{formatCurrency(adv.remainingAmount)}</td>
        <td className="px-5 py-3"><StatusBadge status={adv.status} /></td>
        <td className="px-5 py-3">
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {adv.status === "PENDING" && (
              <>
                <button
                  onClick={() => setModal("approve")}
                  disabled={!canApprove}
                  title={!canApprove ? "You don't have permission to approve" : undefined}
                  className="rounded-lg bg-[#2447d9] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Approve
                </button>
                <button
                  onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-semibold text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </>
            )}
            {adv.status === "APPROVED" && (
              <button
                onClick={() => setModal("release")}
                disabled={!canRelease}
                title={!canRelease ? "You don't have permission to release" : undefined}
                className="rounded-lg bg-[#15803d] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#166534] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Release
              </button>
            )}
            {adv.status === "RELEASED" && (
              <span className="text-xs text-[#16a34a] font-medium">
                Released{adv.releasedAt ? ` ${formatDate(adv.releasedAt)}` : ""}
                {adv.releasedBy ? ` by ${adv.releasedBy}` : ""}
              </span>
            )}
            {adv.status === "REJECTED" && (
              <span className="text-xs text-[#94a3b8]">Rejected</span>
            )}
          </div>
        </td>
        <td className="px-3 py-3 text-[#94a3b8]">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#f8fafc]">
          <td colSpan={6} className="px-5 pb-3 pt-1">
            <div className="text-xs space-y-1 text-[#64748b]">
              {adv.approvedAt && <p><span className="font-medium">Approved:</span> {formatDate(adv.approvedAt)} by {adv.approvedBy}</p>}
              {adv.releasedAt && <p><span className="font-medium">Released:</span> {formatDate(adv.releasedAt)} by {adv.releasedBy}</p>}
              {adv.rejectedAt && <p><span className="font-medium">Rejected:</span> {formatDate(adv.rejectedAt)} by {adv.rejectedBy}</p>}
              {adv.comments && <p><span className="font-medium">Note:</span> {adv.comments}</p>}
            </div>
          </td>
        </tr>
      )}

      {modal === "approve" && (
        <Modal isOpen onClose={() => setModal(null)} title="Approve Wallet Advance" size="sm">
          <ApproveAdvanceModal adv={adv} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "release" && (
        <Modal isOpen onClose={() => setModal(null)} title="Release Wallet Advance" size="sm">
          <ReleaseAdvanceModal adv={adv} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "reject" && (
        <Modal isOpen onClose={() => setModal(null)} title="Reject Wallet Advance" size="sm">
          <RejectAdvanceModal adv={adv} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
    </>
  );
}

// ── Commission Transactions Overview ─────────────────────────────────────────

function TransactionsOverview({ businessId }: { businessId: string }) {
  const [prefix, setPrefix] = useState(new Date().toISOString().slice(0, 7));
  const {
    data: txnPages,
    fetchNextPage: loadMoreTxns,
    hasNextPage: hasMoreTxns,
    isFetchingNextPage: txnsFetchingMore,
    isLoading,
    refetch,
  } = useAdminWalletTransactions(businessId, prefix);
  const txns = txnPages?.pages.flatMap((p) => p.items) ?? [];

  const totalCommissions = txns.reduce((s, t) => s + t.commissionAmount, 0);
  const byStaff = txns.reduce((map, t) => {
    const key = t.userId;
    const existing = map.get(key) ?? { staffName: t.staffName, total: 0, count: 0 };
    map.set(key, { ...existing, total: existing.total + t.commissionAmount, count: existing.count + 1 });
    return map;
  }, new Map<string, { staffName: string; total: number; count: number }>());
  const staffList = Array.from(byStaff.values()).sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Filter by Month</label>
            <input
              type="month"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
            />
          </div>
          <button onClick={() => refetch()} className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:bg-[#f8fafc] transition-colors" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : txns.length === 0 ? (
          <EmptyState
            icon={<TrendingUp className="h-7 w-7 text-[#94a3b8]" />}
            title="No commission transactions"
            description="Commission transactions will appear here once services with commission rates are recorded."
          />
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-[#e2e8f0] bg-[#eff3ff] px-5 py-4">
                <div className="flex items-center gap-2 mb-2">
                  <Banknote className="h-5 w-5 text-[#2447d9]" />
                  <p className="text-xs font-medium text-[#4f46e5]">Total Commissions</p>
                </div>
                <p className="text-2xl font-bold text-[#2447d9]">{formatCurrency(totalCommissions)}</p>
                <p className="text-xs text-[#818cf8] mt-0.5">{txns.length} transactions</p>
              </div>
              <div className="rounded-xl border border-[#e2e8f0] bg-[#f0fdf4] px-5 py-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-[#16a34a]" />
                  <p className="text-xs font-medium text-[#15803d]">Staff Earning</p>
                </div>
                <p className="text-2xl font-bold text-[#16a34a]">{byStaff.size}</p>
                <p className="text-xs text-[#4ade80] mt-0.5">Unique staff members</p>
              </div>
            </div>

            {staffList.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide mb-3">Top Earners</p>
                <div className="space-y-2">
                  {staffList.map((s, i) => {
                    const pct = totalCommissions > 0 ? (s.total / totalCommissions) * 100 : 0;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-28 shrink-0">
                          <p className="text-sm font-medium text-[#0f172a] truncate">{s.staffName}</p>
                          <p className="text-xs text-[#94a3b8]">{s.count} svc</p>
                        </div>
                        <div className="flex-1 h-2 rounded-full bg-[#e2e8f0] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[#2447d9]"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="w-24 text-right text-sm font-semibold text-[#2447d9]">
                          {formatCurrency(s.total)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-[#e2e8f0]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    {["Date", "Staff", "Service", "Rate", "Commission"].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {txns.map((t) => (
                    <tr key={t.txnId} className="hover:bg-[#f8fafc]">
                      <td className="px-5 py-3 text-[#64748b] whitespace-nowrap">{t.date}</td>
                      <td className="px-5 py-3 font-medium text-[#0f172a]">{t.staffName}</td>
                      <td className="px-5 py-3 text-[#64748b]">{t.serviceName}</td>
                      <td className="px-5 py-3 text-[#64748b]">{t.commissionRate}%</td>
                      <td className="px-5 py-3 font-semibold text-[#16a34a]">{formatCurrency(t.commissionAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMoreTxns && (
                <div className="flex justify-center py-3 border-t border-[#f1f5f9]">
                  <button
                    onClick={() => loadMoreTxns()}
                    disabled={txnsFetchingMore}
                    className="text-sm text-[#2447d9] hover:underline font-medium disabled:opacity-50"
                  >
                    {txnsFetchingMore ? "Loading..." : "Show more"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Wallet Advance Limit Form ─────────────────────────────────────────────────

function WalletAdvanceLimitForm({
  businessId,
  currentLimit,
}: {
  businessId: string;
  currentLimit: number | null;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const setLimit = useSetWalletAdvanceLimit();
  const [value, setValue] = useState(currentLimit !== null ? String(currentLimit) : "");

  const handleSave = async () => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) { toast.error("Please enter a valid amount (0 or more)."); return; }
    try {
      await setLimit.mutateAsync({ businessId, walletAdvanceLimit: num });
      toast.success(num === 0 ? "Wallet advance disabled." : `Limit set to ₱${num.toLocaleString("en-PH", { minimumFractionDigits: 2 })}.`);
    } catch {
      toast.error("Failed to save limit. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Maximum Advance Amount (₱)
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#94a3b8]">₱</span>
              <input
                type="number"
                min="0"
                step="100"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. 2000"
                className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-7 pr-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
              />
            </div>
            <Button onClick={handleSave} disabled={setLimit.isPending}>
              {setLimit.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
        {currentLimit !== null && (
          <p className="text-xs text-[#64748b]">
            Current limit: <strong>{currentLimit === 0 ? "Disabled" : `₱${currentLimit.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`}</strong>
          </p>
        )}
        {currentLimit === null && (
          <p className="text-xs text-[#d97706]">Not configured — employees cannot request wallet advances.</p>
        )}
      </div>
    </>
  );
}

// ── Add Credit Modal ──────────────────────────────────────────────────────────

function AddCreditModal({
  businessId,
  onClose,
}: {
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const { data: users = [] } = useBizUsers(businessId);
  const addCredit = useAddWalletCredit();
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!userId) { toast.error("Please select an employee."); return; }
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0."); return; }
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    try {
      await addCredit.mutateAsync({ businessId, payload: { userId, businessId, amount: amt, reason: reason.trim() } });
      toast.success("Wallet credit added successfully.");
      setTimeout(onClose, 1000);
    } catch {
      toast.error("Failed to add wallet credit. Please try again.");
    }
  };

  return (
    <Modal isOpen title="Add Wallet Credit" onClose={onClose} size="sm">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#eff6ff] border border-[#bfdbfe] p-4">
          <p className="text-sm text-[#1e40af]">
            Manually add funds to an employee's wallet balance. This will be recorded in the ledger.
          </p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#475569] mb-1">Employee</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className={inputClass}>
            <option value="">Select employee…</option>
            {users.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#475569] mb-1">Amount (₱)</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#475569] mb-1">Reason</label>
          <input
            type="text"
            placeholder="e.g. Performance bonus, adjustment…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSubmit} disabled={addCredit.isPending} fullWidth>
            {addCredit.isPending ? "Adding…" : "Add Credit"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Approve Service Request Modal ─────────────────────────────────────────────

function ApproveServiceModal({
  req,
  businessId,
  onClose,
}: {
  req: ServiceRequest;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const approve = useApproveServiceRequest();
  const { data: services = [] } = useStoreServices(businessId);

  const serviceConfig = req.serviceId ? services.find((s) => s.serviceId === req.serviceId) : undefined;
  const calcCommission = () => {
    if (!serviceConfig) return "";
    if (serviceConfig.commissionType === "fixed" && serviceConfig.commissionFixed) {
      return String(serviceConfig.commissionFixed);
    }
    const rate = serviceConfig.commissionRate ?? 0;
    if (rate > 0) return String(Math.round(req.amount * rate / 100 * 100) / 100);
    return "";
  };

  const [commissionAmount, setCommissionAmount] = useState(() => calcCommission());
  const [reviewNotes, setReviewNotes] = useState("");

  const parsed = parseFloat(commissionAmount);
  const isValid = !isNaN(parsed) && parsed > 0;

  const handleApprove = async () => {
    if (!isValid) { toast.error("Commission amount must be greater than 0."); return; }
    try {
      await approve.mutateAsync({
        requestId: req.requestId,
        payload: { userId: req.userId, businessId, commissionAmount: parsed, reviewNotes: reviewNotes || undefined },
      });
      toast.success("Service request approved and commission credited!");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to approve. The request may have already been reviewed.");
    }
  };

  const commissionHint = serviceConfig
    ? serviceConfig.commissionType === "fixed"
      ? `Fixed ₱${serviceConfig.commissionFixed?.toLocaleString("en-PH") ?? 0}`
      : `${serviceConfig.commissionRate ?? 0}% of ₱${req.amount.toLocaleString("en-PH")}`
    : null;

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#f0fdf4] border border-[#bbf7d0] p-4">
          <p className="text-xs font-medium text-[#166534] mb-1">{req.employeeName ?? "Employee"}</p>
          <p className="text-sm font-semibold text-[#0f172a]">{req.description}</p>
          <p className="text-xs text-[#16a34a] mt-0.5">Service amount: {formatCurrency(req.amount)}</p>
          {req.notes && (
            <p className="text-xs text-[#64748b] mt-1 pt-1 border-t border-[#bbf7d0]">
              <span className="font-medium text-[#166534]">Notes:</span> {req.notes}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Commission Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#94a3b8]">₱</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={commissionAmount}
              onChange={(e) => setCommissionAmount(e.target.value)}
              placeholder="0.00"
              className={`${inputClass} pl-7`}
            />
          </div>
          {commissionHint && (
            <p className="mt-1 text-xs text-[#2447d9]">Auto-calculated: {commissionHint}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Review Notes (optional)</label>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            rows={3}
            maxLength={300}
            placeholder="Add a note for the employee…"
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleApprove} disabled={approve.isPending || !isValid} fullWidth>
            {approve.isPending ? "Approving…" : "Approve & Credit"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Reject Service Request Modal ──────────────────────────────────────────────

function RejectServiceReqModal({
  req,
  businessId,
  onClose,
}: {
  req: ServiceRequest;
  businessId: string;
  onClose: () => void;
}) {
  const { toasts, toast, dismissToast } = useToast();
  const reject = useRejectServiceRequest();
  const [reviewNotes, setReviewNotes] = useState("");

  const handleReject = async () => {
    try {
      await reject.mutateAsync({
        requestId: req.requestId,
        payload: { userId: req.userId, businessId, reviewNotes: reviewNotes || undefined },
      });
      toast.success("Service request rejected.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to reject. The request may have already been reviewed.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-xl bg-[#fef2f2] border border-[#fecaca] p-4">
          <p className="text-xs font-medium text-[#991b1b] mb-1">{req.employeeName ?? "Employee"}</p>
          <p className="text-sm font-semibold text-[#0f172a]">{req.description}</p>
          <p className="text-xs text-[#fca5a5] mt-0.5">Service amount: {formatCurrency(req.amount)}</p>
          {req.notes && (
            <p className="text-xs text-[#64748b] mt-1 pt-1 border-t border-[#fecaca]">
              <span className="font-medium text-[#991b1b]">Notes:</span> {req.notes}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Reason (optional)</label>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            rows={3}
            maxLength={300}
            placeholder="Reason for rejection…"
            className={inputClass}
          />
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button
            onClick={handleReject}
            disabled={reject.isPending}
            fullWidth
            className="!bg-[#dc2626] hover:!bg-[#b91c1c]"
          >
            {reject.isPending ? "Rejecting…" : "Reject Request"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Service Request Row (admin) ───────────────────────────────────────────────

function VoidServiceRequestModal({ req, businessId, onClose }: { req: ServiceRequest; businessId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const voidMutation = useVoidServiceRequest();

  const handleVoid = async () => {
    try {
      const result = await voidMutation.mutateAsync({ requestId: req.requestId, payload: { userId: req.userId, businessId } });
      const encMsg = result.encashmentsCancelled > 0
        ? ` ${result.encashmentsCancelled} pending encashment${result.encashmentsCancelled > 1 ? "s were" : " was"} also cancelled.`
        : "";
      toast.success(`Service request voided — commission reversed.${encMsg}`);
      setTimeout(onClose, 1200);
    } catch (err) {
      const msg = (err as Error)?.message ?? "";
      if (msg.includes("NOT_APPROVED")) toast.error("This request is no longer approved.");
      else toast.error("Failed to void. Please try again.");
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Void Service Request Approval" size="sm">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        {/* Summary */}
        <div className="rounded-xl bg-[#fef2f2] border border-[#fecaca] px-4 py-3">
          <p className="text-xs text-[#94a3b8] mb-1">Commission being reversed</p>
          <p className="text-lg font-bold text-[#dc2626]">
            {req.commissionAmount != null ? formatCurrency(req.commissionAmount) : "—"}
          </p>
          <p className="text-xs text-[#64748b] mt-0.5">{req.employeeName ?? req.userId} · {req.description}</p>
        </div>

        {/* What will happen */}
        <div className="rounded-lg bg-[#fffbeb] border border-[#fde68a] px-4 py-3 space-y-1.5 text-xs text-[#92400e]">
          <p className="font-semibold mb-0.5">This action will:</p>
          <p>• Void the commission — removes ₱{(req.commissionAmount ?? 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })} from {req.employeeName ?? "employee"}'s wallet balance</p>
          <p>• Return the service request to PENDING for re-review</p>
          <p>• Write a <strong>SERVICE_REQUEST_VOIDED</strong> reversal entry in the ledger</p>
        </div>

        <p className="text-xs text-[#94a3b8]">
          Encashment requests are not touched. If the employee's balance drops below their approved encashments, reject those separately in the Encashment Requests tab.
        </p>

        {voidMutation.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth disabled={voidMutation.isPending}>Cancel</Button>
          <button
            onClick={handleVoid}
            disabled={voidMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#dc2626] px-4 py-2 text-sm font-medium text-white hover:bg-[#b91c1c] disabled:opacity-60 transition-colors"
          >
            {voidMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Yes, Void Approval
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ServiceRequestAdminRow({
  req,
  businessId,
}: {
  req: ServiceRequest;
  businessId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState<"approve" | "reject" | "void" | null>(null);

  const { canApproveAction } = useAuth();
  const canApprove = canApproveAction("wallet:service-request", "approve");
  const canReject = canApproveAction("wallet:service-request", "reject");

  return (
    <>
      <tr
        className="hover:bg-[#f8fafc] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-5 py-3">
          <p className="text-sm font-semibold text-[#0f172a]">{req.employeeName ?? req.userId}</p>
          <p className="text-xs text-[#94a3b8]">{formatDate(req.createdAt)}</p>
        </td>
        <td className="px-5 py-3">
          <p className="text-sm text-[#0f172a]">{req.description}</p>
          <p className="text-xs text-[#94a3b8]">Service: {formatCurrency(req.amount)}</p>
          {req.notes && (
            <p className="text-xs text-[#64748b] mt-0.5 italic max-w-[220px] truncate" title={req.notes}>
              "{req.notes}"
            </p>
          )}
        </td>
        <td className="px-5 py-3 text-sm font-semibold text-[#16a34a]">
          {req.commissionAmount !== undefined ? formatCurrency(req.commissionAmount) : "—"}
        </td>
        <td className="px-5 py-3"><StatusBadge status={req.status} /></td>
        <td className="px-5 py-3">
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {req.status === "PENDING" && (
              <>
                <button
                  onClick={() => setModal("approve")}
                  disabled={!canApprove}
                  title={!canApprove ? "You don't have permission to approve" : undefined}
                  className="rounded-lg bg-[#2447d9] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Approve
                </button>
                <button
                  onClick={() => setModal("reject")}
                  disabled={!canReject}
                  title={!canReject ? "You don't have permission to reject" : undefined}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-1.5 text-xs font-semibold text-[#dc2626] hover:bg-[#fee2e2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reject
                </button>
              </>
            )}
            {req.status === "APPROVED" && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#94a3b8]">Approved {req.reviewedAt ? formatDate(req.reviewedAt) : ""}</span>
                <button
                  onClick={() => setModal("void")}
                  className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-2.5 py-1 text-xs font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors"
                  title="Void this approval — reverses the commission"
                >
                  Void
                </button>
              </div>
            )}
            {req.status === "REJECTED" && (
              <span className="text-xs text-[#94a3b8]">Rejected {req.reviewedAt ? formatDate(req.reviewedAt) : ""}</span>
            )}
          </div>
        </td>
        <td className="px-3 py-3 text-[#94a3b8]">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#f8fafc]">
          <td colSpan={6} className="px-5 pb-3 pt-1">
            <div className="text-xs space-y-1 text-[#64748b]">
              {req.notes && <p><span className="font-medium">Employee notes:</span> {req.notes}</p>}
              {req.reviewedAt && <p><span className="font-medium">Reviewed:</span> {formatDate(req.reviewedAt)} by {req.reviewedBy}</p>}
              {req.reviewNotes && <p><span className="font-medium">Admin note:</span> {req.reviewNotes}</p>}
            </div>
          </td>
        </tr>
      )}

      {modal === "void" && (
        <VoidServiceRequestModal req={req} businessId={businessId} onClose={() => setModal(null)} />
      )}
      {modal === "approve" && (
        <Modal isOpen onClose={() => setModal(null)} title="Approve Service Request" size="sm">
          <ApproveServiceModal req={req} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "reject" && (
        <Modal isOpen onClose={() => setModal(null)} title="Reject Service Request" size="sm">
          <RejectServiceReqModal req={req} businessId={businessId} onClose={() => setModal(null)} />
        </Modal>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type TabKey = "pending" | "all" | "advances" | "transactions" | "credits" | "settings" | "service-requests" | "statement";

// ── Staff Statement Tab ───────────────────────────────────────────────────────

type StatementEntry =
  | { kind: "commission"; date: string; sortKey: string; txn: WalletTransaction }
  | { kind: "encashment"; date: string; sortKey: string; enc: WalletEncashment }
  | { kind: "advance";    date: string; sortKey: string; adv: WalletAdvance }
  | { kind: "credit";     date: string; sortKey: string; cred: WalletCredit };

function BalanceCard({ label, amount, sub, color }: { label: string; amount: number; sub?: string; color: string }) {
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-1 ${color}`}>
      <p className="text-xs font-medium text-[#64748b]">{label}</p>
      <p className="text-xl font-bold text-[#0f172a]">{formatCurrency(amount)}</p>
      {sub && <p className="text-[10px] text-[#94a3b8]">{sub}</p>}
    </div>
  );
}

function kindBadge(kind: StatementEntry["kind"]) {
  const map: Record<StatementEntry["kind"], { label: string; cls: string }> = {
    commission: { label: "Commission",  cls: "bg-[#eff3ff] text-[#2447d9]" },
    encashment: { label: "Encashment",  cls: "bg-[#f0fdf4] text-[#16a34a]" },
    advance:    { label: "Advance",     cls: "bg-[#faf5ff] text-[#7c3aed]" },
    credit:     { label: "Credit",      cls: "bg-[#fff7ed] text-[#ea580c]" },
  };
  const { label, cls } = map[kind];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>;
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    ACTIVE: "bg-[#f0fdf4] text-[#16a34a]",
    VOIDED: "bg-[#fef2f2] text-[#dc2626]",
    PENDING: "bg-[#fefce8] text-[#ca8a04]",
    APPROVED: "bg-[#eff3ff] text-[#2447d9]",
    RELEASED: "bg-[#f0fdf4] text-[#16a34a]",
    REJECTED: "bg-[#fef2f2] text-[#dc2626]",
    PAID: "bg-[#f0fdf4] text-[#16a34a]",
  };
  const cls = map[status] ?? "bg-[#f1f5f9] text-[#64748b]";
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{status}</span>;
}

function StaffStatementTab({ businessId }: { businessId: string }) {
  const { data: users = [], isLoading: usersLoading } = useBizUsers(businessId);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const comboRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.position?.toLowerCase().includes(q),
    );
  }, [users, search]);

  const selectedUser = users.find((u) => u.userId === selectedUserId);

  function selectUser(userId: string) {
    setSelectedUserId(userId);
    setSearch("");
    setOpen(false);
  }

  const { data: statement, isLoading: stmtLoading } = useQuery({
    queryKey: ["wallet-statement", businessId, selectedUserId],
    queryFn: () => walletApi.getEmployeeStatement(businessId, selectedUserId!),
    enabled: !!selectedUserId,
    staleTime: 30_000,
  });

  const timeline = useMemo((): StatementEntry[] => {
    if (!statement) return [];
    const entries: StatementEntry[] = [
      ...statement.transactions.map((t): StatementEntry => ({
        kind: "commission",
        date: t.date,
        sortKey: `${t.date}#${t.createdAt}`,
        txn: t,
      })),
      ...statement.encashments.map((e): StatementEntry => ({
        kind: "encashment",
        date: e.requestedAt.slice(0, 10),
        sortKey: `${e.requestedAt.slice(0, 10)}#${e.requestedAt}`,
        enc: e,
      })),
      ...statement.advances.map((a): StatementEntry => ({
        kind: "advance",
        date: a.requestedAt.slice(0, 10),
        sortKey: `${a.requestedAt.slice(0, 10)}#${a.requestedAt}`,
        adv: a,
      })),
      ...statement.credits.map((c): StatementEntry => ({
        kind: "credit",
        date: c.addedAt.slice(0, 10),
        sortKey: `${c.addedAt.slice(0, 10)}#${c.addedAt}`,
        cred: c,
      })),
    ];
    return entries.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }, [statement]);

  function entryAmount(e: StatementEntry): { amount: number; sign: "+" | "−" } {
    if (e.kind === "commission") return { amount: e.txn.commissionAmount, sign: "+" };
    if (e.kind === "credit")     return { amount: e.cred.amount, sign: "+" };
    if (e.kind === "encashment") return { amount: e.enc.amount, sign: "−" };
    return { amount: e.adv.amount, sign: "−" };
  }

  function entryDescription(e: StatementEntry): string {
    if (e.kind === "commission") return `${e.txn.serviceName} · ${e.txn.date}`;
    if (e.kind === "encashment") return "Encashment request";
    if (e.kind === "advance")    return "Wallet advance";
    return e.cred.reason;
  }

  function entryStatus(e: StatementEntry): string {
    if (e.kind === "commission") return e.txn.status;
    if (e.kind === "encashment") return e.enc.status;
    if (e.kind === "advance")    return e.adv.status;
    return "ACTIVE";
  }

  const bal: WalletBalance | undefined = statement?.balance;

  return (
    <div className="space-y-4">
      {/* Employee combobox */}
      <div className="flex items-center gap-3">
        <div ref={comboRef} className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94a3b8] pointer-events-none" />
          <input
            type="text"
            value={open ? search : (selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : search)}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => { setSearch(""); setOpen(true); }}
            placeholder={usersLoading ? "Loading…" : "Search employee…"}
            disabled={usersLoading}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#e2e8f0] text-sm text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20 disabled:opacity-60"
          />
          {open && filtered.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-[#e2e8f0] bg-white shadow-lg max-h-60 overflow-y-auto">
              {filtered.map((u) => (
                <button
                  key={u.userId}
                  onMouseDown={(e) => { e.preventDefault(); selectUser(u.userId); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-[#f8fafc] transition-colors"
                >
                  <span className="font-medium text-[#0f172a]">{u.firstName} {u.lastName}</span>
                  {u.position && <span className="text-xs text-[#94a3b8] ml-2">{u.position}</span>}
                </button>
              ))}
            </div>
          )}
          {open && !usersLoading && filtered.length === 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-[#e2e8f0] bg-white shadow-lg px-3 py-2 text-sm text-[#94a3b8]">
              No employees found.
            </div>
          )}
        </div>
        {selectedUser && (
          <button
            onClick={() => { setSelectedUserId(null); setSearch(""); }}
            className="text-xs text-[#94a3b8] hover:text-[#64748b] transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Statement */}
      {selectedUserId && (
        <>
          {stmtLoading ? (
            <div className="flex justify-center py-10"><LoadingSpinner /></div>
          ) : statement && bal ? (
            <>
              {/* Balance summary */}
              <div>
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-2">
                  Balance — {selectedUser?.firstName} {selectedUser?.lastName}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <BalanceCard label="Total Earned"        amount={bal.totalEarned}             color="border-[#e2e8f0] bg-white" />
                  <BalanceCard label="Available Balance"   amount={bal.availableBalance}         color="border-[#bfdbfe] bg-[#eff6ff]" />
                  <BalanceCard label="Total Encashed"      amount={bal.releasedEncashment}       sub="Released only" color="border-[#bbf7d0] bg-[#f0fdf4]" />
                  <BalanceCard label="Outstanding Advance" amount={bal.outstandingWalletAdvances} color="border-[#ddd6fe] bg-[#faf5ff]" />
                </div>
                {(bal.pendingEncashment > 0 || bal.approvedEncashment > 0) && (
                  <div className="mt-2 flex gap-3">
                    {bal.pendingEncashment > 0 && (
                      <p className="text-xs text-[#ca8a04]">Pending encashment: {formatCurrency(bal.pendingEncashment)}</p>
                    )}
                    {bal.approvedEncashment > 0 && (
                      <p className="text-xs text-[#2447d9]">Approved (not yet released): {formatCurrency(bal.approvedEncashment)}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Timeline */}
              <Card>
                <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#0f172a]">Transaction History</p>
                  <p className="text-xs text-[#94a3b8]">{timeline.length} entries</p>
                </div>
                {timeline.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-[#94a3b8]">No transactions found.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#f1f5f9] bg-[#f8fafc] text-left text-xs font-medium text-[#64748b]">
                          <th className="px-5 py-3">Date</th>
                          <th className="px-5 py-3">Type</th>
                          <th className="px-5 py-3">Description</th>
                          <th className="px-5 py-3 text-right">Amount</th>
                          <th className="px-5 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#f1f5f9]">
                        {timeline.map((entry, i) => {
                          const { amount, sign } = entryAmount(entry);
                          return (
                            <tr key={i} className="hover:bg-[#f8fafc]">
                              <td className="px-5 py-3 whitespace-nowrap text-[#64748b]">{entry.date}</td>
                              <td className="px-5 py-3">{kindBadge(entry.kind)}</td>
                              <td className="px-5 py-3 text-[#374151] max-w-[260px] truncate">{entryDescription(entry)}</td>
                              <td className={`px-5 py-3 text-right font-semibold whitespace-nowrap ${sign === "+" ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                                {sign}{formatCurrency(amount)}
                              </td>
                              <td className="px-5 py-3">{statusBadge(entryStatus(entry))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function AdminWalletPage() {
  const { activeBusinessId } = useAuth();
  const [tab, setTab] = useState<TabKey>("all");
  const [statusFilter, setStatusFilter] = useState<EncashmentStatus>("PENDING");
  const [advanceStatusFilter, setAdvanceStatusFilter] = useState<WalletAdvanceStatus>("PENDING");
  const [serviceReqStatusFilter, setServiceReqStatusFilter] = useState<ServiceRequestStatus>("PENDING");
  const [showAddCredit, setShowAddCredit] = useState(false);

  const {
    data: pendingData,
    refetch: refetchPending,
  } = useAdminEncashments(activeBusinessId, "PENDING");
  const {
    data: filteredData,
    isLoading: filteredLoading,
    refetch: refetchFiltered,
    fetchNextPage: loadMoreFiltered,
    hasNextPage: hasMoreFiltered,
    isFetchingNextPage: filteredFetchingMore,
  } = useAdminEncashments(activeBusinessId, statusFilter);

  const {
    data: advancesData,
    isLoading: advancesLoading,
    fetchNextPage: loadMoreAdvances,
    hasNextPage: hasMoreAdvances,
    isFetchingNextPage: advancesFetchingMore,
  } = useAdminWalletAdvances(activeBusinessId, advanceStatusFilter);

  const {
    data: pendingAdvancesData,
  } = useAdminWalletAdvances(activeBusinessId, "PENDING");

  // Approved counts — needed for tab badges on Encashments and Advances
  const { data: approvedEncsData } = useAdminEncashments(activeBusinessId, "APPROVED");
  const { data: approvedAdvancesData } = useAdminWalletAdvances(activeBusinessId, "APPROVED");

  const { data: walletConfig, isLoading: configLoading } = useWalletAdvanceConfig(activeBusinessId);
  const { data: adminCredits = [], isLoading: creditsLoading, refetch: refetchCredits } = useAdminWalletCredits(activeBusinessId);
  const { data: serviceRequests = [], isLoading: serviceReqsLoading } = useAdminServiceRequests(activeBusinessId, serviceReqStatusFilter);
  const { data: pendingServiceReqsData = [] } = useAdminServiceRequests(activeBusinessId, "PENDING");

  const pending = pendingData?.pages.flatMap((p) => p.items) ?? [];
  const filteredEncs = filteredData?.pages.flatMap((p) => p.items) ?? [];
  const advances = advancesData?.pages.flatMap((p) => p.items) ?? [];
  const pendingAdvancesCount  = pendingAdvancesData?.pages.flatMap((p) => p.items).length ?? 0;
  const approvedEncsCount     = approvedEncsData?.pages.flatMap((p) => p.items).length ?? 0;
  const approvedAdvancesCount = approvedAdvancesData?.pages.flatMap((p) => p.items).length ?? 0;
  const pendingServiceReqCount = pendingServiceReqsData.length;

  if (!activeBusinessId) {
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Wallet className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Wallet</h1>
              <p className="text-xs text-[#64748b]">Review encashment requests and track commission payouts</p>
            </div>
          </div>
        </div>
        <EmptyState
          icon={<Wallet className="h-8 w-8 text-[#94a3b8]" />}
          title="No active business"
          description="Select a business from the top bar to manage wallet requests."
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Wallet className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Wallet</h1>
              <p className="text-xs text-[#64748b]">Review encashment requests, advances, and commission payouts</p>
            </div>
          </div>
          <button
            onClick={() => { refetchPending(); refetchFiltered(); }}
            title="Refresh"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:text-[#64748b] hover:bg-[#f8fafc] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Per-area summary tiles — each navigates to its tab */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {/* Encashment Requests */}
          <button
            type="button"
            onClick={() => { setTab("all"); setStatusFilter("PENDING"); }}
            className={`text-left rounded-xl border px-3 py-2.5 transition-colors hover:border-[#2447d9] ${
              tab === "all" ? "border-[#2447d9] bg-[#eff3ff]" : "border-[#e2e8f0] bg-[#f8fafc]"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Wallet className="h-3 w-3 text-[#2447d9]" />
              <p className="text-[10px] font-semibold text-[#2447d9] uppercase tracking-wide">Encashments</p>
            </div>
            <div className="space-y-0.5">
              {pending.length > 0 && (
                <p className="text-xs font-semibold text-[#a16207]">{pending.length} pending</p>
              )}
              {approvedEncsCount > 0 && (
                <p className="text-xs font-semibold text-[#1d4ed8]">{approvedEncsCount} approved</p>
              )}
              {pending.length === 0 && approvedEncsCount === 0 && (
                <p className="text-xs text-[#94a3b8]">All clear</p>
              )}
            </div>
          </button>

          {/* Wallet Advances */}
          <button
            type="button"
            onClick={() => { setTab("advances"); setAdvanceStatusFilter("PENDING"); }}
            className={`text-left rounded-xl border px-3 py-2.5 transition-colors hover:border-[#7c3aed] ${
              tab === "advances" ? "border-[#7c3aed] bg-[#f5f3ff]" : "border-[#e2e8f0] bg-[#f8fafc]"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <CreditCard className="h-3 w-3 text-[#7c3aed]" />
              <p className="text-[10px] font-semibold text-[#7c3aed] uppercase tracking-wide">Advances</p>
            </div>
            <div className="space-y-0.5">
              {pendingAdvancesCount > 0 && (
                <p className="text-xs font-semibold text-[#a16207]">{pendingAdvancesCount} pending</p>
              )}
              {approvedAdvancesCount > 0 && (
                <p className="text-xs font-semibold text-[#1d4ed8]">{approvedAdvancesCount} approved</p>
              )}
              {pendingAdvancesCount === 0 && approvedAdvancesCount === 0 && (
                <p className="text-xs text-[#94a3b8]">All clear</p>
              )}
            </div>
          </button>

          {/* Service Requests */}
          <button
            type="button"
            onClick={() => { setTab("service-requests"); setServiceReqStatusFilter("PENDING"); }}
            className={`col-span-2 sm:col-span-1 text-left rounded-xl border px-3 py-2.5 transition-colors hover:border-[#16a34a] ${
              tab === "service-requests" ? "border-[#16a34a] bg-[#f0fdf4]" : "border-[#e2e8f0] bg-[#f8fafc]"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Clipboard className="h-3 w-3 text-[#16a34a]" />
              <p className="text-[10px] font-semibold text-[#16a34a] uppercase tracking-wide">Service Requests</p>
            </div>
            <div className="space-y-0.5">
              {pendingServiceReqCount > 0 && (
                <p className="text-xs font-semibold text-[#a16207]">{pendingServiceReqCount} pending</p>
              )}
              {pendingServiceReqCount === 0 && (
                <p className="text-xs text-[#94a3b8]">All clear</p>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Tabs — Pending tab removed; Encashment Requests is now the default */}
      <div className="border-b border-[#e2e8f0] overflow-x-auto overflow-y-hidden">
        <div className="flex gap-1 min-w-max">
          {([
            { key: "all" as TabKey,               label: "Encashment Requests", icon: <Wallet className="h-4 w-4" />,      badge: pending.length + approvedEncsCount },
            { key: "advances" as TabKey,          label: "Wallet Advances",     icon: <CreditCard className="h-4 w-4" />,  badge: pendingAdvancesCount + approvedAdvancesCount },
            { key: "service-requests" as TabKey,  label: "Service Requests",    icon: <Clipboard className="h-4 w-4" />,   badge: pendingServiceReqCount },
            // { key: "transactions" as TabKey, label: "Commission Overview", icon: <BarChart2 className="h-4 w-4" />, badge: 0 },
            { key: "credits" as TabKey,           label: "Wallet Credits",      icon: <PlusCircle className="h-4 w-4" />,  badge: 0 },
            { key: "statement" as TabKey,         label: "Staff Statement",     icon: <FileText className="h-4 w-4" />,    badge: 0 },
            { key: "settings" as TabKey,          label: "Settings",            icon: <Settings className="h-4 w-4" />,    badge: 0 },
          ]).map(({ key, label, icon, badge }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                tab === key ? "border-[#2447d9] text-[#2447d9]" : "border-transparent text-[#64748b] hover:text-[#0f172a]"
              }`}
            >
              {icon}{label}
              {badge > 0 && (
                <span className="ml-1 inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-[#dc2626] text-[10px] font-bold text-white">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* All requests tab */}
      {tab === "all" && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            {(["PENDING", "APPROVED", "RELEASED", "REJECTED"] as EncashmentStatus[]).map((s) => {
              const count = s === "PENDING" ? pending.length : s === "APPROVED" ? approvedEncsCount : undefined;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${
                    statusFilter === s
                      ? "bg-[#2447d9] text-white"
                      : "border border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f5f9]"
                  }`}
                >
                  {s.charAt(0) + s.slice(1).toLowerCase()}
                  {count !== undefined && count > 0 && (
                    <span className={`inline-flex items-center justify-center rounded-full min-w-4 px-1 h-4 text-[10px] font-bold ${
                      statusFilter === s ? "bg-white/20 text-white" : "bg-[#dc2626] text-white"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <Card padding="none">
            {filteredLoading ? (
              <div className="flex justify-center py-10"><LoadingSpinner /></div>
            ) : filteredEncs.length === 0 ? (
              <EmptyState
                icon={<Wallet className="h-7 w-7 text-[#94a3b8]" />}
                title={`No ${statusFilter.toLowerCase()} requests`}
                description={`There are currently no ${statusFilter.toLowerCase()} encashment requests.`}
                className="py-10"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      {["Employee", "Amount", "Status", "Actions", ""].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {filteredEncs.map((e) => (
                      <EncashmentRow key={e.encashId} enc={e} businessId={activeBusinessId} />
                    ))}
                  </tbody>
                </table>
                {hasMoreFiltered && (
                  <div className="flex justify-center py-3 border-t border-[#f1f5f9]">
                    <button
                      onClick={() => loadMoreFiltered()}
                      disabled={filteredFetchingMore}
                      className="text-sm text-[#2447d9] hover:underline font-medium disabled:opacity-50"
                    >
                      {filteredFetchingMore ? "Loading..." : "Show more"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Wallet Advances tab */}
      {tab === "advances" && (
        <div className="space-y-4">
          {/* Status filter buttons */}
          <div className="flex gap-2 flex-wrap">
            {(["PENDING", "APPROVED", "RELEASED", "REJECTED"] as WalletAdvanceStatus[]).map((s) => {
              const count = s === "PENDING" ? pendingAdvancesCount : s === "APPROVED" ? approvedAdvancesCount : undefined;
              return (
                <button
                  key={s}
                  onClick={() => setAdvanceStatusFilter(s)}
                  className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${
                    advanceStatusFilter === s
                      ? "bg-[#2447d9] text-white"
                      : "border border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f5f9]"
                  }`}
                >
                  {s.charAt(0) + s.slice(1).toLowerCase()}
                  {count !== undefined && count > 0 && (
                    <span className={`inline-flex items-center justify-center rounded-full min-w-4 px-1 h-4 text-[10px] font-bold ${
                      advanceStatusFilter === s ? "bg-white/20 text-white" : "bg-[#dc2626] text-white"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <Card padding="none">
            {advancesLoading ? (
              <div className="flex justify-center py-10"><LoadingSpinner /></div>
            ) : advances.length === 0 ? (
              <EmptyState
                icon={<CreditCard className="h-7 w-7 text-[#94a3b8]" />}
                title={`No ${advanceStatusFilter.toLowerCase()} advances`}
                description="Wallet advance requests will appear here."
                className="py-10"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      {["Employee", "Amount", "Remaining", "Status", "Actions", ""].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {advances.map((a) => (
                      <AdvanceRow key={a.advId} adv={a} businessId={activeBusinessId} />
                    ))}
                  </tbody>
                </table>
                {hasMoreAdvances && (
                  <div className="flex justify-center py-3 border-t border-[#f1f5f9]">
                    <button
                      onClick={() => loadMoreAdvances()}
                      disabled={advancesFetchingMore}
                      className="text-sm text-[#2447d9] hover:underline font-medium disabled:opacity-50"
                    >
                      {advancesFetchingMore ? "Loading..." : "Show more"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Service Requests tab */}
      {tab === "service-requests" && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            {(["PENDING", "APPROVED", "REJECTED"] as ServiceRequestStatus[]).map((s) => {
              const count = s === "PENDING" ? pendingServiceReqCount : undefined;
              return (
                <button
                  key={s}
                  onClick={() => setServiceReqStatusFilter(s)}
                  className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${
                    serviceReqStatusFilter === s
                      ? "bg-[#2447d9] text-white"
                      : "border border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f5f9]"
                  }`}
                >
                  {s.charAt(0) + s.slice(1).toLowerCase()}
                  {count !== undefined && count > 0 && (
                    <span className={`inline-flex items-center justify-center rounded-full min-w-4 px-1 h-4 text-[10px] font-bold ${
                      serviceReqStatusFilter === s ? "bg-white/20 text-white" : "bg-[#dc2626] text-white"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <Card padding="none">
            {serviceReqsLoading ? (
              <div className="flex justify-center py-10"><LoadingSpinner /></div>
            ) : serviceRequests.length === 0 ? (
              <EmptyState
                icon={<Clipboard className="h-7 w-7 text-[#94a3b8]" />}
                title={`No ${serviceReqStatusFilter.toLowerCase()} service requests`}
                description="Employee service requests will appear here."
                className="py-10"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      {["Employee", "Description", "Commission", "Status", "Actions", ""].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {serviceRequests.map((r) => (
                      <ServiceRequestAdminRow key={r.requestId} req={r} businessId={activeBusinessId} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Commission overview tab */}
      {tab === "transactions" && (
        <TransactionsOverview businessId={activeBusinessId} />
      )}

      {/* Wallet Credits tab */}
      {tab === "credits" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-base font-semibold text-[#0f172a]">Wallet Credits</h2>
              <p className="text-xs text-[#64748b]">Admin-added funds credited directly to employee wallets.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => refetchCredits()} className="flex items-center gap-1.5 text-sm">
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
              <Button onClick={() => setShowAddCredit(true)} className="flex items-center gap-1.5 text-sm">
                <PlusCircle className="h-4 w-4" /> Add Credit
              </Button>
            </div>
          </div>
          <Card padding="none">
            {creditsLoading ? (
              <div className="flex justify-center py-10"><LoadingSpinner /></div>
            ) : adminCredits.length === 0 ? (
              <EmptyState
                icon={<PlusCircle className="h-7 w-7 text-[#94a3b8]" />}
                title="No credits yet"
                description="No wallet credits have been added for this business."
                className="py-10"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      {["Employee", "Amount", "Reason", "Added By", "Date"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#64748b]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adminCredits.map((c) => (
                      <tr key={c.creditId} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors">
                        <td className="px-4 py-3 font-medium text-[#0f172a]">{c.employeeName ?? "—"}</td>
                        <td className="px-4 py-3 text-[#16a34a] font-semibold">{formatCurrency(c.amount)}</td>
                        <td className="px-4 py-3 text-[#475569] max-w-xs truncate">{c.reason}</td>
                        <td className="px-4 py-3 text-[#64748b]">{c.addedBy}</td>
                        <td className="px-4 py-3 text-[#64748b]">{formatDate(c.addedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          {showAddCredit && (
            <AddCreditModal businessId={activeBusinessId} onClose={() => setShowAddCredit(false)} />
          )}
        </div>
      )}

      {/* Settings tab */}
      {tab === "statement" && activeBusinessId && (
        <StaffStatementTab businessId={activeBusinessId} />
      )}

      {tab === "settings" && (
        <div className="max-w-lg space-y-6">
          <Card>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-[#0f172a] mb-0.5">Wallet Advance Limit</h3>
                <p className="text-xs text-[#64748b]">
                  Maximum total outstanding wallet advance an employee can have at any time. Set to 0 to disable wallet advances.
                </p>
              </div>
              {configLoading ? (
                <LoadingSpinner />
              ) : (
                <WalletAdvanceLimitForm
                  businessId={activeBusinessId}
                  currentLimit={walletConfig?.walletAdvanceLimit ?? null}
                />
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
